import type { EngageState, UnpostedItem } from "~/store/engageSlice";

import logger from "~/helpers/logger";

export interface LegacyQueuedPostItem {
	itemId: string;
	itemName: string;
	note?: string;
	quantity: number;
	sku: string;
}

export interface LegacyQueuedPost {
	id: string;
	items?: LegacyQueuedPostItem[];
	locationId?: string;
	queuedAt: string;
	status: "failed" | "pending" | "posted" | "posting";
}

export interface LegacyEngageState extends EngageState {
	postQueue?: LegacyQueuedPost[];
}

const isUnsyncedLegacyPost = (post: LegacyQueuedPost): boolean => post.status !== "posted";

const toUnpostedItem = (line: LegacyQueuedPostItem): UnpostedItem => ({
	itemId: line.itemId,
	name: line.itemName,
	...(line.note !== undefined ? { note: line.note } : {}),
	quantity: line.quantity,
	sku: line.sku,
	upc: ""
});

const restageUnsyncedPosts = (
	unpostedCounts: Record<string, UnpostedItem[]>,
	postQueue: LegacyQueuedPost[]
): Record<string, UnpostedItem[]> =>
	postQueue.filter(isUnsyncedLegacyPost).reduce<Record<string, UnpostedItem[]>>((restaged, post) => {
		const lines = (post.items ?? []).map(toUnpostedItem);
		if (post.locationId === undefined || lines.length === 0) return restaged;
		return { ...restaged, [post.locationId]: [...(restaged[post.locationId] ?? []), ...lines] };
	}, unpostedCounts);

const reportRestagedLegacyPosts = (postQueue: LegacyQueuedPost[]): void => {
	const unsynced = postQueue.filter(isUnsyncedLegacyPost);
	if (unsynced.length === 0) return;
	logger.error("[engage] legacy postQueue entries restaged as unposted counts — they were fired without a frozen key", {
		restagedPosts: unsynced.map(({ id, locationId, queuedAt, status }) => ({ id, locationId, queuedAt, status }))
	});
};

const migrateLegacyEngagePostQueue = (outboundState: LegacyEngageState): EngageState => {
	const { postQueue, ...rehydratedState } = outboundState;
	const pendingPosts = rehydratedState.pendingPosts ?? {};
	if (postQueue === undefined) return { ...rehydratedState, pendingPosts };
	reportRestagedLegacyPosts(postQueue);
	return {
		...rehydratedState,
		pendingPosts,
		unpostedCounts: restageUnsyncedPosts(rehydratedState.unpostedCounts ?? {}, postQueue)
	};
};

export default migrateLegacyEngagePostQueue;
